import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import * as alignmentcomments from 'analyses/alignment/AlignmentComments';
import CreateSequenceAlignmentsModal from 'analyses/alignment/modals/CreateSequenceAlignmentsModal';
import {
  AlignmentInput,
  InputType,
  SequenceAnalysisInfo,
} from 'analyses/alignment/modals/createSequenceAlignmentsModalReducer';
import AlignmentTabMenu, {AlignmentTabMenuProps} from 'analyses/AlignmentTabMenu';
import {Alignment, AlignmentAnalysis} from 'analyses/Analyses';
import * as analytics from 'analytics/Analytics';
import {track} from 'analytics/Analytics';
import GapSequence from 'biojs/GapSequence';
// @ts-expect-error
import PreviewModalView from 'components/PreviewModalView';
import {Selection, Sequence} from 'components/Sequence';
import {AlignmentPolymerType} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import AlignmentPDFDownloadModal from 'molbio/AlignmentPDFDownloadModal';
import {generateRawSequencesFromDNAAlignment} from 'molbio/AlignmentPDFHelpers/GenerateRawSequencesFromAlignment';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import * as paginate from 'molbio/pdf/Paginate';
import Pagination from 'molbio/pdf/Pagination';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import legacyRunModal from 'ui-library/legacyRunModal';
import {toFilename} from 'util/FileUtil';
import {Optional} from 'util/Types';

import * as alignmentviewutil from './AlignmentViewUtil';
import {getNextMismatch, getPreviousMismatch} from './Mismatches';

// Because uuid.v4 is not patchable, wrap it here so this can be stubbed in the tests.
export const getUUID = (): string => uuidv4();

function getAlignmentInput(sequence: GapSequence, isBioentity: boolean, rowNumber: number): AlignmentInput {
  return isBioentity
    ? {
        type: InputType.BIOENTITY,
        name: sequence.get('name'),
        id: sequence.get('id'),
        canBeTemplate: sequence.get('permissions').polymerFeaturesEditable!,
        useCurrentVersion: true,
        nucleotideType: sequence.nucleotideType(),
      }
    : {
        type: InputType.TEXT,
        name: sequence.get('name'),
        id: getUUID(),
        residues: sequence.cloneWithoutGaps().get('bases'),
        alignmentRowNumber: rowNumber,
        nucleotideType: sequence.nucleotideType(),
      };
}

function getSequenceAnalysisInfo(
  alignmentAnalysis: AlignmentAnalysis,
  sequence: Sequence
): SequenceAnalysisInfo {
  let template: Optional<Alignment>;
  const items: Array<AlignmentInput> = [];
  alignmentAnalysis.get('alignments').forEach((alignment: Alignment, alignmentRowIndex: number) => {
    if (alignment.get('type') === 'template') {
      template = alignment;
    }
    if (alignment.get('type') !== 'template' && alignment.get('type') !== 'consensus') {
      items.push(
        getAlignmentInput(alignment.get('sequence'), alignment.get('existingSequence'), alignmentRowIndex)
      );
    }
  });
  const alignmentSetup = template
    ? {
        template: getAlignmentInput(
          template.get('sequence'),
          template.get('existingSequence'),
          alignmentAnalysis.get('alignments').indexOf(template)
        ),
        items,
      }
    : {
        name: alignmentAnalysis.get('name'),
        items,
        // All inputs in a consensus alignment should have the same nucleotide type; this is enforced in
        // DefineParametersPane
        nucleotideType: items[0].nucleotideType,
      };

  return {
    sequenceAnalysisId: alignmentAnalysis.get('apiId'),
    sequenceId: sequence.id,
    alignmentSetup,
  };
}

export default class SequenceAlignmentTabOptionsView extends Backbone.View<SequenceAlignmentTabState> {
  static initClass(): void {
    this.prototype.className = 'reset';
    this.prototype.tagName = 'div';
  }

  sequence: Sequence | undefined;

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this.listenTo(this.model, 'change', this.render.bind(this));
    this.listenTo(this.model, 'change:loaded', () => {
      if (this.model.get('loaded')) {
        this._bindListenersToAnalysis();
        this.listenTo(this.getSelection(), 'change', this.render.bind(this));
      } else if (this.getSelection()) {
        this.stopListening(this.getSelection());
      }
    });
    if (this.model.get('loaded')) {
      this._bindListenersToAnalysis();
    }
    this.listenTo(this.sequence.get('versions'), 'reloaded change:name', this.render.bind(this));
    this.listenTo(this.sequence, 'change:writable change:permissions', this.render.bind(this));
  }

  _bindListenersToAnalysis(): void {
    const analysis = this.model.get('analysis')!;
    this.listenTo(analysis.get('alignments'), 'remove', this.render.bind(this));
  }

  /**
   * @return {Selection?} - the Backbone.Model for the template/consensus alignments' selection, which is
   *   synced to all other selection. Note that this can be `null` if the analysis is not yet loaded.
   */
  getSelection(): Selection | null {
    const mapstate = this.model.get('mapstates').models[0];
    return mapstate ? mapstate.get('sequence').get('selection') : null;
  }

  async updateName(newName: string): Promise<void> {
    const analysis = this.model.get('analysis')!;
    await analysis.save({name: newName}, {parse: false, patch: true});
    this.model.set('title', analysis.get('name'));
    alignmentviewutil.notifySuccess(analysis.attributes);
  }

  addComment(): void {
    const selection = this.getSelection() as Selection;
    const alignmentComments = this.model.get('analysis')!.get('alignmentComments');
    if (selection.length() === 0) {
      const length = selection.get('sequence').length();
      if (selection.start() === length) {
        selection.setEndpoints(length - 1, length);
      } else {
        selection.setEndpoints(selection.start(), selection.start() + 1);
      }
    }

    const comment = new alignmentcomments.AlignmentComment({start: selection.start(), end: selection.end()});
    alignmentComments.add(comment);
    comment.trigger('open', comment);
  }

  sortRows(sortByPosition: boolean): void {
    // Takes comparator for normal alignments; topValue defined such that it always
    // beats what comparator returns. We do this because numeric sorting is different
    // from string sorting.
    // Top sequence should remain where it is. Used below also.
    const topId = $('.fixed-top').find('.alignment-row').attr('data-id');
    const alignments = this.model.get('analysis')!.get('alignments');
    alignments.comparator = (alignment: Alignment) => {
      if (alignment.cid === topId) {
        return sortByPosition ? 0 : '0';
      }

      if (sortByPosition) {
        return 1 + Math.max(alignment.get('trimStart'), alignment.get('sequence').realToGap(0));
      }

      return `1${alignment.get('sequence').get('name')}`;
    };
    // eslint-disable-next-line no-restricted-syntax
    alignments.sort();
    alignments.trigger('save');
  }

  openRealignModal(): void {
    track('realignment', 'openModal', {source: 'alignment-tab-view'});
    legacyRunModal(CreateSequenceAlignmentsModal, {
      initialInputs: [],
      realignmentInput: {
        sequenceAnalysisInfo: getSequenceAnalysisInfo(
          this.model.get('analysis')!,
          this.model.get('sequence')
        ),
        isTemplateSequenceEditable: this.model.get('sequence').permissions().polymerResiduesEditable || false,
      },
    });
  }

  goToMismatch(direction: 'next' | 'previous'): void {
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.USED_ALIGNMENT_MISMATCH_NAVIGATION, {direction});
    const mismatch = direction === 'next' ? getNextMismatch(this.model) : getPreviousMismatch(this.model);
    if (mismatch) {
      this.model.jumpToLocation(mismatch);
    }
  }

  openPDFSnapshotPreview(): void {
    const trackMetadata = {
      source: 'alignment',
      sequence_api_id: this.model.get('sequence').get('_sequence').id,
      sequence_length: this.model.get('analysis')!.get('alignments').seqLength(),
      alignment_id: this.model.get('analysis')!.id,
    };

    const tabstate = this.model;
    const modalView = new PreviewModalView({
      paginate(css: string | null): Pagination {
        return paginate.alignmentPaginate(tabstate, css);
      },
      landscape: true,
      trackMetadata,
      filename: toFilename(`${this.model.get('analysis')!.name()}-alignment`, 'pdf'),
    });
    modalView.render();
    analytics.track('alignment', 'opened_pdf_preview', trackMetadata);
  }

  openPDFFormattedPreview(): void {
    const rawAlignmentData = generateRawSequencesFromDNAAlignment(this.model);
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_FORMATTED_PDF_MODAL, {
      residueType: AlignmentPolymerType.DNA,
    });
    const selection = this.model.get('selectionProps');
    legacyRunModal(AlignmentPDFDownloadModal, {
      rawAlignmentData,
      alignmentName: this.model.get('analysis')!.name()!,
      alignmentPolymerType: AlignmentPolymerType.DNA,
      selection: {
        start: selection ? selection.get('start') : 0,
        end: selection ? selection.get('end') : null,
      },
    });
  }

  getClustalFormatData(): string {
    const analysis = this.model.get('analysis');
    return analysis ? analysis.clustalFormat() : '';
  }

  getFastaFormatData(): string {
    const analysis = this.model.get('analysis');
    return analysis ? analysis.fastaFormat() : '';
  }

  getProps(): AlignmentTabMenuProps {
    const analysis = this.model.get('analysis');

    return {
      addComment: this.addComment.bind(this),
      alignmentApiId: analysis?.get('apiId'),
      alignmentType: analysis?.get('alignmentType')!,
      allowFeatureEdits: this.model.get('sequence').allowFeatureEdits(),
      allowResidueEdits: this.model.get('sequence').allowResidueEdits(),
      analysisName: analysis?.get('name'),
      attachedFileCount: analysis?.get('attached_file_count') || 0,
      getClustalFormatData: this.getClustalFormatData.bind(this),
      getFastaFormatData: this.getFastaFormatData.bind(this),
      goToMismatch: this.goToMismatch.bind(this),
      loaded: this.model.get('loaded'),
      onToggleSearch: () => {
        const searchDNAData = SearchDNAData.get();
        const isSearchActive = searchDNAData.get('mode') != null;
        searchDNAData.trigger(isSearchActive ? 'close' : 'open');
      },
      openPDFFormattedPreview: this.openPDFFormattedPreview.bind(this),
      openPDFSnapshotPreview: this.openPDFSnapshotPreview.bind(this),
      openRealignModal: this.openRealignModal.bind(this),
      selectionLength: this.getSelection() ? this.getSelection()!.length() : 0,
      sequenceEditURL: this.model.get('sequence').get('editURL'),
      sortRows: this.sortRows.bind(this),
      updateName: this.updateName.bind(this),
      visibilityOptions: this.model.get('visibilityOptions'),
    };
  }

  override render(): SequenceAlignmentTabOptionsView {
    renderToDOM(<AlignmentTabMenu {...this.getProps()} />, this.el);
    return this;
  }
}
SequenceAlignmentTabOptionsView.initClass();
